import { Basket } from './search/index/type';

// Bump the suffix if the persisted shape changes so an old payload is discarded rather than half-read.
export const BASKET_STORAGE_KEY = 'altitude:manual-booking:basket:v4';

/**
 * How long a basket may sit before it is dropped. Shopped offers keep their price and availability
 * only for as long as the backend holds them (3h in Redis), and a basket left open for hours quietly
 * accumulates fares the airline has since repriced or pulled. 45 minutes keeps every basket well
 * inside that window while still surviving a normal interruption mid-booking.
 */
export const BASKET_TTL_MS = 45 * 60 * 1000;

/** How long before expiry the checkout page starts warning the user that the basket is about to clear. */
export const BASKET_EXPIRY_WARNING_MS = 10 * 60 * 1000;

export const emptyBasket: Basket = { flights: [], accommodations: [], vehicles: [], flightsCommitted: false };

export type PersistedBasket = {
  /** The basket as stored, or an empty basket when nothing valid and unexpired was found. */
  basket: Basket;
  /** Epoch ms when the basket first became non-empty, or null while it is empty. Drives expiry. */
  filledAt: number | null;
  /**
   * Whether the user has reached checkout for this basket. Gates the header shortcut, which is a way back to a page
   * already seen rather than an announcement, so it stays hidden until then. Persisted because a reload would
   * otherwise strand a basket the user can no longer navigate to. Resets whenever the basket empties.
   */
  checkoutSeen: boolean;
};

/** Emptiness is about items only: `flightsCommitted` is a flag about the flights, not an item. */
export function isBasketEmpty(basket: Basket): boolean {
  return basket.flights.length === 0 && basket.accommodations.length === 0 && basket.vehicles.length === 0;
}

/**
 * The moment a basket filled at `filledAt` should clear, or null while the basket has never been
 * filled and so has no clock running.
 */
export function basketExpiresAt(filledAt: number | null): number | null {
  return filledAt === null ? null : filledAt + BASKET_TTL_MS;
}

/**
 * Whole minutes left before the basket clears, rounded up so the final partial minute still reads as
 * "1 minute" rather than "0" while the basket is genuinely still usable. Null when nothing is due to expire.
 */
export function basketMinutesRemaining(expiresAt: number | null, now: number): number | null {
  if (expiresAt === null) {
    return null;
  }

  return Math.max(0, Math.ceil((expiresAt - now) / 60_000));
}

/** Whether checkout should be warning the user that the basket is about to clear under them. */
export function isBasketExpiryImminent(expiresAt: number | null, now: number): boolean {
  if (expiresAt === null) {
    return false;
  }

  return expiresAt - now <= BASKET_EXPIRY_WARNING_MS && expiresAt > now;
}

/**
 * Advances the expiry clock for a basket that is about to be stored. The clock is deliberately not
 * slidable: it starts when the basket first gains an item and is never pushed out by later edits, so
 * no offer can sit in the basket for longer than one TTL. Emptying the basket stops the clock.
 */
export function nextFilledAt(previousFilledAt: number | null, basket: Basket, now: number): number | null {
  if (isBasketEmpty(basket)) {
    return null;
  }

  return previousFilledAt ?? now;
}

/**
 * Reads the persisted basket, dropping it if it has outlived its TTL, and tolerating a missing,
 * corrupt or partial payload (storage is best-effort). An expired payload is deleted on the way out
 * so a later read in the same session cannot resurrect it.
 */
export function readStoredBasket(now: number): PersistedBasket {
  try {
    const raw = localStorage.getItem(BASKET_STORAGE_KEY);
    if (!raw) {
      return { basket: emptyBasket, filledAt: null, checkoutSeen: false };
    }

    const parsed = JSON.parse(raw) as Partial<PersistedBasket>;
    const storedBasket = (parsed.basket ?? {}) as Partial<Basket>;
    const basket: Basket = {
      flights: Array.isArray(storedBasket.flights) ? storedBasket.flights : [],
      accommodations: Array.isArray(storedBasket.accommodations) ? storedBasket.accommodations : [],
      vehicles: Array.isArray(storedBasket.vehicles) ? storedBasket.vehicles : [],
      flightsCommitted: storedBasket.flightsCommitted === true,
    };

    if (isBasketEmpty(basket)) {
      return { basket: emptyBasket, filledAt: null, checkoutSeen: false };
    }

    // A payload written before this key existed (or hand-edited) has no clock, so its age can't be
    // proven. Treat it as expired rather than granting it a fresh 45 minutes it may not deserve.
    const filledAt = typeof parsed.filledAt === 'number' ? parsed.filledAt : null;
    const expiresAt = basketExpiresAt(filledAt);
    if (expiresAt === null || now >= expiresAt) {
      clearStoredBasket();
      return { basket: emptyBasket, filledAt: null, checkoutSeen: false };
    }

    return { basket, filledAt, checkoutSeen: parsed.checkoutSeen === true };
  } catch {
    return { basket: emptyBasket, filledAt: null, checkoutSeen: false };
  }
}

/** Mirrors the basket, its expiry clock and whether checkout was reached, so all three survive a reload. */
export function writeStoredBasket(basket: Basket, filledAt: number | null, checkoutSeen: boolean): void {
  try {
    localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify({ basket, filledAt, checkoutSeen }));
  } catch {
    // Quota or serialisation failure — the in-memory basket still works for this session.
  }
}

/** Removes the persisted basket outright, used when it expires. */
export function clearStoredBasket(): void {
  try {
    localStorage.removeItem(BASKET_STORAGE_KEY);
  } catch {
    // Storage unavailable — the in-memory basket is still cleared by the caller.
  }
}
